import React, { memo } from 'react';
import { Chip } from '@mui/material';
import type { TreatmentCategory } from '../../../../domain/reservationSlots';
import { TREATMENT_CATEGORY_LABELS } from '../../../../domain/reservationSlots';

/**
 * CategoryChip のProps型
 *
 * @property {TreatmentCategory} category - 表示する診療カテゴリ
 * @property {number} [count] - 枠数（指定時は「ピル ×2」形式で表示）
 * @property {'small' | 'medium'} [size] - チップサイズ（既定はsmall）
 */
export interface CategoryChipProps {
  category: TreatmentCategory;
  count?: number;
  size?: 'small' | 'medium';
}

/**
 * 診療カテゴリと表示色のマッピング
 *
 * @description
 * 解放枠管理画面全体でカテゴリを一目で識別できるようにするための配色。
 * MUIのセマンティックカラー（error/warning等）はカテゴリの意味と対応しないため、
 * 淡色背景＋濃色文字の組み合わせを直接指定する。
 * EDとその他は同一のグレー系で表示する。
 */
export const CATEGORY_COLORS: Record<TreatmentCategory, { bg: string; fg: string }> = {
  pill: { bg: '#FEE2E2', fg: '#B91C1C' },
  skin: { bg: '#FEF3C7', fg: '#92400E' },
  wellness_clinic: { bg: '#E0F5F5', fg: '#0E7C7B' },
  aga_care: { bg: '#DBEAFE', fg: '#1D4ED8' },
  ed: { bg: '#EEF0F2', fg: '#8A8F98' },
  other: { bg: '#EEF0F2', fg: '#8A8F98' },
};

/**
 * 診療カテゴリ表示チップコンポーネント
 *
 * @description
 * 診療カテゴリをカテゴリ固有の色で表示する。countを渡すと枠数付きで表示する。
 * - count未指定: 「ピル」
 * - count指定時: 「ピル ×2」
 *
 * @param {CategoryChipProps} props - コンポーネントProps
 * @returns {React.ReactElement} カテゴリチップ
 */
const CategoryChip: React.FC<CategoryChipProps> = memo(({ category, count, size = 'small' }) => {
  const { bg, fg } = CATEGORY_COLORS[category];
  const label =
    count === undefined
      ? TREATMENT_CATEGORY_LABELS[category]
      : `${TREATMENT_CATEGORY_LABELS[category]} ×${count}`;

  return (
    <Chip
      label={label}
      size={size}
      sx={{
        backgroundColor: bg,
        color: fg,
        fontWeight: 700,
        borderRadius: 1,
      }}
    />
  );
});

CategoryChip.displayName = 'CategoryChip';

export default CategoryChip;
